import { ContentType, OperationOutcomeError } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Identifier, QuestionnaireResponse } from '@medplum/fhirtypes';
import type { SignAppointmentNoteResult } from '@unify/fhir-model/resources/appointment';
import type { AppointmentCategory } from '../features/calendar/saveCalendarAppointment';
import { UserFacingError } from './userFacingError';

const SCHEDULE_APPOINTMENT_BOT_IDENTIFIER: Identifier = {
  system: 'https://unify.health/bots',
  value: 'appointment-tools',
};

/** Note signing runs as the signing practitioner on its own bot, not the project-admin scheduling bot. */
const SIGN_APPOINTMENT_NOTE_BOT_IDENTIFIER: Identifier = {
  system: 'https://unify.health/bots',
  value: 'sign-appointment-note',
};

function getBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_SCHEDULE_APPOINTMENT_BOT_ID?.trim();
  if (id) return id;
  return {
    system:
      import.meta.env.VITE_MEDPLUM_SCHEDULE_APPOINTMENT_BOT_IDENTIFIER_SYSTEM?.trim()
      ?? SCHEDULE_APPOINTMENT_BOT_IDENTIFIER.system,
    value:
      import.meta.env.VITE_MEDPLUM_SCHEDULE_APPOINTMENT_BOT_IDENTIFIER_VALUE?.trim()
      ?? SCHEDULE_APPOINTMENT_BOT_IDENTIFIER.value,
  };
}

/**
 * Allowlist of the bot's authored schedule conflicts (room/device/location names and times only,
 * never a patient). Mirrors the conflict() templates in medplum-server/src/bots/appointment-tools.ts
 * (~lines 242, 267/1500 signed-note locks; ~758-780 inactive; ~818 booked) verbatim; update both together.
 * The time range is the bot's toLocaleTimeString('en-US') output, e.g. "10:00 AM-11:00 AM".
 */
const TIME = '(?:[1-9]|1[0-2]):[0-5]\\d[ \\u202F\\u00A0][AP]M';
const NAME = '([^\\n]+?)';

/**
 * Fixed clauses of the bot templates, each a literal substring of appointment-tools.ts.
 * Exported so a test can fail when the bot copy drifts from this allowlist.
 */
export const SCHEDULE_BOOKED_CLAUSE = ' is booked ';
export const SCHEDULE_INACTIVE_CLAUSE = ' is inactive and cannot be booked';
export const SCHEDULE_TIME_LOCKED_SENTENCE =
  'Cannot change appointment time or participants after the linked clinical note is signed.';
export const SCHEDULE_STATUS_LOCKED_SENTENCE =
  'Cannot change appointment status after the linked clinical note is signed.';

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const SCHEDULE_CONFLICT_PATTERNS = [
  new RegExp(`^Room ${NAME}${escapeRegExp(SCHEDULE_BOOKED_CLAUSE)}${TIME}-${TIME}$`),
  new RegExp(`^Device ${NAME}${escapeRegExp(SCHEDULE_BOOKED_CLAUSE)}${TIME}-${TIME}$`),
  new RegExp(`^Room ${NAME}${escapeRegExp(SCHEDULE_INACTIVE_CLAUSE)}$`),
  new RegExp(`^Location ${NAME}${escapeRegExp(SCHEDULE_INACTIVE_CLAUSE)}$`),
  new RegExp(`^Device ${NAME}${escapeRegExp(SCHEDULE_INACTIVE_CLAUSE)}$`),
  new RegExp(`^${escapeRegExp(SCHEDULE_TIME_LOCKED_SENTENCE)}$`),
  new RegExp(`^${escapeRegExp(SCHEDULE_STATUS_LOCKED_SENTENCE)}$`),
];

function scheduleConflictText(err: unknown): string | undefined {
  if (!(err instanceof OperationOutcomeError)) return undefined;
  const issue = err.outcome.issue?.[0];
  for (const text of [issue?.details?.text, issue?.diagnostics]) {
    if (text && SCHEDULE_CONFLICT_PATTERNS.some((pattern) => pattern.test(text))) return text;
  }
  return undefined;
}

/** Runs a schedule write; surfaces the bot's allowlisted conflict text as a `UserFacingError`. */
async function executeScheduleWrite<T>(medplum: MedplumClient, input: Record<string, unknown>): Promise<T> {
  try {
    return (await medplum.executeBot(getBotTarget(), input, ContentType.JSON)) as T;
  } catch (err) {
    const text = scheduleConflictText(err);
    throw text ? new UserFacingError(text) : err;
  }
}

export interface ScheduledAppointmentArgs {
  start: Date;
  end: Date;
  calendarStatus: string;
  practitionerId: string;
  practitionerDisplay?: string;
  siteId?: string;
  roomId?: string;
  deviceIds?: string[];
  organizationId?: string;
  patientId?: string;
  patientDisplay?: string;
  episodeOfCareId?: string;
  cptCode?: string;
  cptDisplay?: string;
  serviceTypeCode?: string;
  serviceTypeDisplay?: string;
  serviceTypeSystem?: string;
  /** HealthcareService id for clinical session/group appointments (catalog round-trip). */
  sessionHealthcareServiceId?: string;
  /** HealthcareService.name shown as the appointment's Session Type. */
  sessionHealthcareServiceDisplay?: string;
  appointmentCategory?: AppointmentCategory;
  modality?: string;
  description?: string;
  videoLink?: string;
  recurringPattern?: string;
  additionalParticipants?: Array<{
    resourceType: 'Practitioner' | 'Patient' | 'RelatedPerson';
    id: string;
    display?: string;
  }>;
  therapyGroupId?: string;
  therapyGroupDisplay?: string;
  /** Keep Patient/Practitioner participants the scheduling form does not render. */
  preserveExistingParticipants?: boolean;
}

export async function createScheduledAppointment(
  medplum: MedplumClient,
  args: ScheduledAppointmentArgs,
): Promise<Appointment> {
  return executeScheduleWrite(medplum, { action: 'create-appointment', ...args });
}

export async function saveScheduledAppointmentAttendance(
  medplum: MedplumClient,
  appointmentId: string,
  args: {
    attendance?: Array<{ participantReference: string; attendanceStatus: string }>;
    clinicianCancel?: boolean;
    attendanceSource?: 'manual';
  },
): Promise<Appointment> {
  return executeScheduleWrite(medplum, { action: 'save-attendance', appointmentId, ...args });
}

/**
 * Sign `note` via bot `sign-appointment-note`, which checks it as the signer; the admin
 * `appointment-tools` `sign-and-seal-appointment` action then persists the signature in the note
 * compartment and locks the Appointment (lock check runs as admin over all linked notes).
 * A returned result with `sealed: false` means the signature committed but the seal did not.
 */
export async function signScheduledAppointmentNote(
  medplum: MedplumClient,
  appointmentId: string,
  note: QuestionnaireResponse,
  attendance: Array<{ participantReference: string; attendanceStatus: string }>,
  options: { revision?: boolean } = {},
): Promise<SignAppointmentNoteResult | Appointment> {
  return medplum.executeBot(
    SIGN_APPOINTMENT_NOTE_BOT_IDENTIFIER,
    { appointmentId, note, attendance, ...(options.revision ? { revision: true } : {}) },
    ContentType.JSON,
  ) as Promise<SignAppointmentNoteResult | Appointment>;
}

/**
 * Retry only the Appointment seal for a note that is already signed (never re-signs). The seal is
 * bound to `noteVersionId`; the bot answers 409 when the stored note is at another version.
 */
export async function sealScheduledAppointmentNote(
  medplum: MedplumClient,
  appointmentId: string,
  noteId: string,
  noteVersionId: string | undefined,
  attendance: Array<{ participantReference: string; attendanceStatus: string }>,
): Promise<Appointment> {
  return medplum.executeBot(
    getBotTarget(),
    {
      action: 'seal-appointment',
      appointmentId,
      noteId,
      ...(noteVersionId ? { noteVersionId } : {}),
      attendance,
    },
    ContentType.JSON,
  ) as Promise<Appointment>;
}

export async function updateScheduledAppointment(
  medplum: MedplumClient,
  appointmentId: string,
  args: ScheduledAppointmentArgs,
): Promise<Appointment> {
  return executeScheduleWrite(medplum, { action: 'update-appointment', appointmentId, ...args });
}
